import React, { useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  AccessRoute,
  AdminRoutes,
  AuthProtected,
  HeadTeacherAdminRoutes,
  PublicRoutes,
  SuperAdminRoutes,
  SystemAdminRoutes,
  TeacherAdminRoutes,
} from ".";

import Layout from "../layouts/Layout";
import { NonAuthLayout } from "../layouts";
import { useLazyGetUserProfileQuery } from "../store/api/accountApi";
import { RoleEnum } from "../enums";
import { AppLogo } from "../components/Common/AppLogo";
import { layoutModeTypes } from "../components/constants/layout";
import { AccountState } from "../store/slices/accountSlice";
import "./AppRoutes.scss";

export const AppRoutes = () => {
  const publicPathsArr = PublicRoutes.map((r: any) => r.path);
  const superAdminPaths = SuperAdminRoutes.map((r: any) => r.path);
  const systemAdminPaths = SystemAdminRoutes.map((r: any) => r.path);
  const teacherAdminPaths = TeacherAdminRoutes.map((r: any) => r.path);
  const headTeacherAdminPaths = HeadTeacherAdminRoutes.map((r: any) => r.path);
  const adminPaths = AdminRoutes.map((r: any) => r.path);

  // Use Redux auth token so profile fetch runs immediately after setCredentials (no wait on useSession)
  const token = useSelector((state: any) => state.auth?.token ?? null);

  // Use profile from account slice so logout clears it; query only fetches and syncs
  const profile = useSelector(
    (state: { account: AccountState }) => state.account?.userProfile,
  );

  const [getUserProfile, { isLoading: isLoadingProfile }] =
    useLazyGetUserProfileQuery();

  const layoutModeType = useSelector(
    (state: { Layout: { layoutModeType: string } }) =>
      state.Layout.layoutModeType,
  );
  const isDarkMode = layoutModeType === layoutModeTypes.DARKMODE;

  useEffect(() => {
    if (token && !profile) {
      document.documentElement.setAttribute(
        "data-layout-mode",
        isDarkMode ? layoutModeTypes.DARKMODE : layoutModeTypes.LIGHTMODE,
      );
    }
  }, [token, profile, isDarkMode]);

  useEffect(() => {
    if (token && !profile && !isLoadingProfile) {
      getUserProfile();
    }
  }, [token, profile]);

  if (token && !profile) {
    return (
      <div className="app-routes-splash">
        <AppLogo width={150} />
      </div>
    );
  }

  return (
    <React.Fragment>
      <Switch>
        {profile && profile.role.key === RoleEnum.SUPER_ADMIN && (
          <Route path={superAdminPaths}>
            <AuthProtected>
              <Layout>
                <Switch>
                  {SuperAdminRoutes.map((route, idx) => (
                    <AccessRoute
                      path={route.path}
                      component={route.component}
                      allowedRoles={route.allowedRoles}
                      key={idx}
                      exact={route.exact ?? true}
                    />
                  ))}
                </Switch>
              </Layout>
            </AuthProtected>
          </Route>
        )}
        {profile && profile.role.key === RoleEnum.SYSTEM_ADMIN && (
          <Route path={systemAdminPaths}>
            <AuthProtected>
              <Layout>
                <Switch>
                  {SystemAdminRoutes.map((route, idx) => (
                    <AccessRoute
                      path={route.path}
                      component={route.component}
                      key={idx}
                      exact={route.exact ?? true}
                    />
                  ))}
                </Switch>
              </Layout>
            </AuthProtected>
          </Route>
        )}
        {profile && profile.role.key === RoleEnum.TEACHER_ADMIN && (
          <Route path={teacherAdminPaths}>
            <AuthProtected>
              <Layout>
                <Switch>
                  {TeacherAdminRoutes.map((route, idx) => (
                    <AccessRoute
                      path={route.path}
                      component={route.component}
                      key={idx}
                      exact={route.exact ?? true}
                    />
                  ))}
                </Switch>
              </Layout>
            </AuthProtected>
          </Route>
        )}
        {profile && profile.role.key === RoleEnum.HEAD_TEACHER_ADMIN && (
          <Route path={headTeacherAdminPaths}>
            <AuthProtected>
              <Layout>
                <Switch>
                  {HeadTeacherAdminRoutes.map((route, idx) => (
                    <AccessRoute
                      path={route.path}
                      component={route.component}
                      key={idx}
                      exact={route.exact ?? true}
                    />
                  ))}
                </Switch>
              </Layout>
            </AuthProtected>
          </Route>
        )}
        {profile && profile.role.key === RoleEnum.ADMIN && (
          <Route path={adminPaths}>
            <AuthProtected>
              <Layout>
                <Switch>
                  {AdminRoutes.map((route, idx) => (
                    <AccessRoute
                      path={route.path}
                      component={route.component}
                      key={idx}
                      exact={route.exact ?? true}
                    />
                  ))}
                </Switch>
              </Layout>
            </AuthProtected>
          </Route>
        )}
        <Route path={publicPathsArr}>
          <NonAuthLayout>
            <Switch>
              {PublicRoutes.map((route: any, idx: number) => (
                <Route
                  path={route.path}
                  component={route.component}
                  key={idx}
                  exact={route.exact ?? true}
                />
              ))}
              <Route path="*">
                <Redirect to="/login" />
              </Route>
            </Switch>
          </NonAuthLayout>
        </Route>
        <Route path="*">
          <Redirect to="/login" />
        </Route>
      </Switch>
    </React.Fragment>
  );
};
